import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL, getAuthHeaders } from '../../config/api.config';
import { formatDistanceToNow } from 'date-fns';

interface AuditLog {
  id: number;
  user: {
    id: number | null;
    name: string | null;
    email: string | null;
  };
  action: string;
  action_description: string;
  entity_type: string;
  entity_id: number | null;
  changes: Record<string, { old: unknown; new: unknown }>;
  metadata: Record<string, unknown> | null;
  ip_address: string | null;
  user_agent: string | null;
  is_high_risk: boolean;
  created_at: string;
}

interface AuditLogResponse {
  data: AuditLog[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

interface AuditFilters {
  user_id?: string;
  action?: string;
  entity_type?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
  per_page?: number;
}

interface AuditStats {
  period: string;
  date_range: {
    start: string;
    end: string;
  };
  total_activities: number;
  unique_users: number;
  actions_breakdown: Record<string, number>;
  entities_breakdown: Record<string, number>;
  high_risk_activities: number;
  activity_trend: Record<string, number>;
}

const AuditTrailViewer: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<AuditFilters>({
    per_page: 50
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0
  });
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [showStats, setShowStats] = useState(false);
  const [stats, setStats] = useState<AuditStats | null>(null);
  const [users, setUsers] = useState<Array<{ id: number; name: string; email: string }>>([]);

  const actionTypes = [
    { value: 'create', label: 'Create' },
    { value: 'update', label: 'Update' },
    { value: 'delete', label: 'Delete' },
    { value: 'view', label: 'View' },
    { value: 'export', label: 'Export' },
    { value: 'rule_change', label: 'Rule Change' },
    { value: 'bulk_operation', label: 'Bulk Operation' },
    { value: 'login', label: 'Login' },
    { value: 'logout', label: 'Logout' }
  ];

  const fetchAuditLogs = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value.toString());
      });
      params.append('page', page.toString());

      const response = await fetch(`${API_BASE_URL}/v2/admin/audit-trail?${params}`, {
        headers: getAuthHeaders() as HeadersInit
      });

      if (!response.ok) {
        throw new Error('Failed to fetch audit logs');
      }

      const result = await response.json();
      const data = result.data as AuditLogResponse;
      
      setLogs(data.data);
      setPagination({
        currentPage: data.current_page,
        lastPage: data.last_page,
        total: data.total
      });

      // Extract unique users for filter dropdown
      const uniqueUsers = new Map<number, { id: number; name: string; email: string }>();
      data.data.forEach(log => {
        if (log.user.id) {
          uniqueUsers.set(log.user.id, {
            id: log.user.id,
            name: log.user.name || 'Unknown',
            email: log.user.email || ''
          });
        }
      });
      setUsers(Array.from(uniqueUsers.values()));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchStats = async (period = 'month') => {
    try {
      const response = await fetch(`${API_BASE_URL}/v2/admin/audit-trail/stats?period=${period}`, {
        headers: getAuthHeaders() as HeadersInit
      });

      if (!response.ok) {
        throw new Error('Failed to fetch audit statistics');
      }

      const result = await response.json();
      setStats(result.data);
      setShowStats(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load statistics');
    }
  };

  const exportAuditLogs = async (format = 'json') => {
    try {
      const response = await fetch(`${API_BASE_URL}/v2/admin/audit-trail/export`, {
        method: 'POST',
        headers: {
          ...(getAuthHeaders() as HeadersInit),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ filters, format })
      });

      if (!response.ok) {
        throw new Error('Failed to export audit logs');
      }

      const result = await response.json();
      
      // For JSON, create a downloadable file
      if (format === 'json') {
        const blob = new Blob([JSON.stringify(result.data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `audit-logs-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export audit logs');
    }
  };

  useEffect(() => {
    fetchAuditLogs();
  }, [fetchAuditLogs]);

  const handleFilterChange = (key: keyof AuditFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value || undefined }));
  };

  const handlePageChange = (page: number) => {
    fetchAuditLogs(page);
  };

  const getActionClass = (action: string): string => {
    const classes: Record<string, string> = {
      create: 'bg-green-100 text-green-800',
      update: 'bg-blue-100 text-blue-800',
      delete: 'bg-red-100 text-red-800',
      view: 'bg-gray-100 text-gray-800',
      export: 'bg-purple-100 text-purple-800',
      rule_change: 'bg-orange-100 text-orange-800',
      bulk_operation: 'bg-yellow-100 text-yellow-800',
      login: 'bg-indigo-100 text-indigo-800',
      logout: 'bg-indigo-100 text-indigo-800'
    };
    return classes[action] || 'bg-gray-100 text-gray-800';
  };

  const renderChangeDetails = (changes: Record<string, { old: unknown; new: unknown }>) => {
    if (!changes || Object.keys(changes).length === 0) {
      return <span className="text-gray-500">No changes recorded</span>;
    }

    return (
      <div className="space-y-2">
        {Object.entries(changes).map(([field, change]) => (
          <div key={field} className="text-sm">
            <span className="font-medium">{field}:</span>
            <div className="ml-4">
              <div className="text-red-600">- {JSON.stringify(change.old)}</div>
              <div className="text-green-600">+ {JSON.stringify(change.new)}</div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg">
        <div className="p-6 border-b">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold">Audit Trail</h1>
            <div className="space-x-2">
              <button
                onClick={() => fetchStats('month')}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                View Statistics
              </button>
              <button
                onClick={() => exportAuditLogs('json')}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                Export Logs
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">User</label>
              <select
                value={filters.user_id || ''}
                onChange={(e) => handleFilterChange('user_id', e.target.value)}
                className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Users</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.name} ({user.email})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
              <select
                value={filters.action || ''}
                onChange={(e) => handleFilterChange('action', e.target.value)}
                className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Actions</option>
                {actionTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date From</label>
              <input
                type="date"
                value={filters.date_from || ''}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
                className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date To</label>
              <input
                type="date"
                value={filters.date_to || ''}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
                className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <input
                type="text"
                value={filters.search || ''}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Search..."
                className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Audit Log Table */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading audit logs...</p>
            </div>
          ) : error ? (
            <div className="p-8 text-center text-red-600">
              <p>{error}</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Entity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    IP Address
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {logs.map((log) => (
                  <tr key={log.id} className={log.is_high_risk ? 'bg-red-50' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>
                        {new Date(log.created_at).toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {log.user.name || 'System'}
                      {log.user.email && (
                        <div className="text-xs text-gray-500">{log.user.email}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${getActionClass(log.action)}`}>
                        {log.action_description}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {log.entity_type}
                      {log.entity_id && ` #${log.entity_id}`}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {log.ip_address || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <button
                        onClick={() => setSelectedLog(log)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {!loading && logs.length > 0 && (
          <div className="px-6 py-4 border-t flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {(pagination.currentPage - 1) * (filters.per_page || 50) + 1} to{' '}
              {Math.min(pagination.currentPage * (filters.per_page || 50), pagination.total)} of{' '}
              {pagination.total} results
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
                className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              {[...Array(Math.min(5, pagination.lastPage))].map((_, i) => {
                const page = i + 1;
                return (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-3 py-1 border rounded ${
                      page === pagination.currentPage
                        ? 'bg-blue-600 text-white'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
              {pagination.lastPage > 5 && <span className="px-2">...</span>}
              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.lastPage}
                className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="text-xl font-bold">Audit Log Details</h2>
              <button
                onClick={() => setSelectedLog(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold text-gray-700">User</h3>
                  <p>{selectedLog.user.name || 'System'}</p>
                  {selectedLog.user.email && (
                    <p className="text-sm text-gray-500">{selectedLog.user.email}</p>
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700">Timestamp</h3>
                  <p>{new Date(selectedLog.created_at).toLocaleString()}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700">Action</h3>
                  <span className={`px-2 py-1 text-xs rounded-full ${getActionClass(selectedLog.action)}`}>
                    {selectedLog.action_description}
                  </span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700">Entity</h3>
                  <p>{selectedLog.entity_type} {selectedLog.entity_id && `#${selectedLog.entity_id}`}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700">IP Address</h3>
                  <p>{selectedLog.ip_address || '-'}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700">Risk Level</h3>
                  <p className={selectedLog.is_high_risk ? 'text-red-600 font-semibold' : 'text-green-600'}>
                    {selectedLog.is_high_risk ? 'High Risk' : 'Normal'}
                  </p>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-gray-700 mb-2">Changes</h3>
                {renderChangeDetails(selectedLog.changes)}
              </div>

              {selectedLog.metadata && (
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Additional Information</h3>
                  <pre className="bg-gray-50 p-4 rounded text-sm overflow-x-auto">
                    {JSON.stringify(selectedLog.metadata, null, 2)}
                  </pre>
                </div>
              )}

              {selectedLog.user_agent && (
                <div>
                  <h3 className="font-semibold text-gray-700">User Agent</h3>
                  <p className="text-sm text-gray-600">{selectedLog.user_agent}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Statistics Modal */}
      {showStats && stats && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="text-xl font-bold">Audit Statistics</h2>
              <button
                onClick={() => setShowStats(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="bg-blue-50 p-4 rounded">
                  <h3 className="text-sm font-medium text-blue-800">Total Activities</h3>
                  <p className="text-2xl font-bold text-blue-900">{stats.total_activities}</p>
                  <p className="text-xs text-blue-600 mt-1">
                    {stats.date_range.start} - {stats.date_range.end}
                  </p>
                </div>
                <div className="bg-green-50 p-4 rounded">
                  <h3 className="text-sm font-medium text-green-800">Unique Users</h3>
                  <p className="text-2xl font-bold text-green-900">{stats.unique_users}</p>
                </div>
                <div className="bg-red-50 p-4 rounded">
                  <h3 className="text-sm font-medium text-red-800">High Risk Activities</h3>
                  <p className="text-2xl font-bold text-red-900">{stats.high_risk_activities}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold mb-3">Actions Breakdown</h3>
                  <div className="space-y-2">
                    {Object.entries(stats.actions_breakdown).map(([action, count]) => (
                      <div key={action} className="flex justify-between items-center">
                        <span className={`px-2 py-1 text-xs rounded-full ${getActionClass(action)}`}>
                          {action}
                        </span>
                        <span className="font-medium">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold mb-3">Entities Breakdown</h3>
                  <div className="space-y-2">
                    {Object.entries(stats.entities_breakdown).map(([entity, count]) => (
                      <div key={entity} className="flex justify-between items-center">
                        <span className="text-gray-700">{entity}</span>
                        <span className="font-medium">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditTrailViewer;